import { Loading, TableCard, TableOutput } from "@general-backoffice/core";
import type { PaymentMethod } from "@investhinks/api-client";
import React from "react";
import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";

import { TypedTable } from "../../utils/typedLib";
import AddButtonChildren from "../shared/AddButtonChildren";

interface PaymentMethodsCardProps {
  paymentMethods?: PaymentMethod[];
  setPaymentMethodToCreate?: () => void;
  setPaymentMethodToRemove?: (paymentMethod: PaymentMethod) => void;
  isLoading?: boolean;
}

const PaymentMethodsCard: React.FC<PaymentMethodsCardProps> = ({
  paymentMethods,
  setPaymentMethodToCreate,
  setPaymentMethodToRemove,
  isLoading,
}) => {
  const { t } = useTranslation("paymentMethods", { keyPrefix: "paymentMethodsTableCard" });

  const columns = [
    {
      name: t("headers.last4"),
      formattedContent: ({ pm_details }: PaymentMethod) => `**** **** **** ${pm_details?.last4}`,
      isTh: true,
    },
    {
      name: t("headers.expiration"),
      formattedContent: ({ pm_details }: PaymentMethod) => {
        return `${pm_details?.exp_month ?? "--"}/${pm_details?.exp_year ?? "--"}`;
      },
    },
    {
      name: "",
      formattedContent: (paymentMethod: PaymentMethod) => {
        const actions = [
          {
            icon: <i className="fas fa-trash table-action-delete" />,
            description: t("removeDescription"),
            onClick: () => setPaymentMethodToRemove?.({ ...paymentMethod }),
          },
        ];
        return <TableOutput.CustomActions actions={actions} />;
      },
    },
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header
        title={t("title")}
        buttons={[
          {
            children: <AddButtonChildren>{t("createBtn")}</AddButtonChildren>,
            onClick: setPaymentMethodToCreate,
            disabled: !setPaymentMethodToCreate,
          },
        ]}
        isLoading={isLoading}
      />
      {isLoading && <Loading.Line />}
      <TypedTable
        columns={columns}
        rows={paymentMethods || []}
        isLoading={isLoading}
        noContentMessage={t("empty")}
        selectable={false}
        sortable={false}
      />
    </Card>
  );
};

export default PaymentMethodsCard;
